import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Check, X, RefreshCw, User as UserIcon, Sparkles } from "lucide-react";

/**
 * Admin queue for user-suggested foods.
 * Backend: /api/admin/food-suggestions{?status=pending|approved|rejected|all}
 *          /api/admin/food-suggestions/{id}/approve  { promote_to_global, corrected_name_bg?, ... }
 *          /api/admin/food-suggestions/{id}/reject   { reason }
 */
export default function FoodSuggestionsManager() {
  const { API } = useAuth();
  const { lang } = useLanguage();
  const bg = lang === "bg";

  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowBusy, setRowBusy] = useState({});   // { id: 'approving'|'rejecting' }
  const [rejectReason, setRejectReason] = useState({});
  const [promoteFlag, setPromoteFlag] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/food-suggestions?status=${filter}&limit=200`);
      setItems(r.data.items || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || (bg ? "Грешка при зареждане." : "Failed to load.");
      toast.error(msg);
    }
    setLoading(false);
  }, [API, filter, bg]);

  useEffect(() => { load(); }, [load]);

  const approve = async (item) => {
    setRowBusy(p => ({ ...p, [item.id]: "approving" }));
    try {
      await axios.post(`${API}/admin/food-suggestions/${item.id}/approve`, {
        promote_to_global: !!promoteFlag[item.id],
      });
      toast.success(bg ? "Одобрено." : "Approved.");
      setItems(prev => prev.filter(x => x.id !== item.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || (bg ? "Грешка." : "Error."));
    }
    setRowBusy(p => ({ ...p, [item.id]: null }));
  };

  const reject = async (item) => {
    const reason = (rejectReason[item.id] || "").trim();
    if (reason.length < 2) {
      toast.error(bg ? "Задължителна причина за отказ." : "Rejection reason required.");
      return;
    }
    setRowBusy(p => ({ ...p, [item.id]: "rejecting" }));
    try {
      await axios.post(`${API}/admin/food-suggestions/${item.id}/reject`, { reason });
      toast.success(bg ? "Отхвърлено." : "Rejected.");
      setItems(prev => prev.filter(x => x.id !== item.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || (bg ? "Грешка." : "Error."));
    }
    setRowBusy(p => ({ ...p, [item.id]: null }));
  };

  const filters = [
    { key: "pending", label_bg: "Чакащи",  label_en: "Pending"  },
    { key: "approved", label_bg: "Одобрени", label_en: "Approved" },
    { key: "rejected", label_bg: "Отказани", label_en: "Rejected" },
    { key: "all",      label_bg: "Всички",   label_en: "All"      },
  ];

  return (
    <div className="space-y-4" data-testid="food-suggestions-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-600" />
            {bg ? "Предложения за храна от потребители" : "User Food Suggestions"}
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            {bg
              ? "Прегледайте и одобрете храни, предложени от потребители, за да ги добавите в общата база."
              : "Review user-submitted foods and approve them into the global database."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {filters.map(f => (
            <button
              key={f.key}
              data-testid={`suggest-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                filter === f.key
                  ? "bg-emerald-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
              }`}
            >
              {bg ? f.label_bg : f.label_en}
            </button>
          ))}
          <Button variant="outline" size="sm" onClick={load} data-testid="suggest-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            {bg ? "Няма предложения в тази категория." : "No suggestions in this category."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="suggest-list">
          {items.map(item => {
            const busy = rowBusy[item.id];
            const per = item.per100g || {};
            return (
              <Card key={item.id} className="border-l-4 border-l-emerald-500" data-testid={`suggest-item-${item.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-sm truncate">{item.name_bg}</h3>
                        {item.name_en && (
                          <span className="text-xs text-muted-foreground">/ {item.name_en}</span>
                        )}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                          item.status === "pending" ? "bg-amber-100 text-amber-700" :
                          item.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                          "bg-red-100 text-red-700"
                        }`}>
                          {item.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <UserIcon className="w-3 h-3" />
                          {item.user_email || "—"}
                        </span>
                        {item.category && <span>· {item.category}</span>}
                        {item.created_at && (
                          <span>· {String(item.created_at).slice(0, 10)}</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
                        <MacroPill label="kcal" value={per.calories} />
                        <MacroPill label="P" value={per.protein} unit="g" />
                        <MacroPill label="C" value={per.carbs}   unit="g" />
                        <MacroPill label="F" value={per.fat}     unit="g" />
                      </div>
                      {item.notes && (
                        <p className="text-xs text-muted-foreground mt-2 italic">
                          {bg ? "Бележки:" : "Notes:"} {item.notes}
                        </p>
                      )}
                      {item.reject_reason && (
                        <p className="text-xs text-red-600 mt-2">
                          {bg ? "Причина за отказ:" : "Reject reason:"} {item.reject_reason}
                        </p>
                      )}
                    </div>

                    {item.status === "pending" && (
                      <div className="flex flex-col gap-2 sm:min-w-[240px]">
                        <label className="flex items-center gap-2 text-[11px] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!promoteFlag[item.id]}
                            onChange={e => setPromoteFlag(p => ({ ...p, [item.id]: e.target.checked }))}
                            data-testid={`promote-checkbox-${item.id}`}
                          />
                          <span>{bg ? "Промотирай в глобалната база" : "Promote to global DB"}</span>
                        </label>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => approve(item)}
                          disabled={!!busy}
                          data-testid={`approve-btn-${item.id}`}
                        >
                          {busy === "approving" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                          {bg ? "Одобри" : "Approve"}
                        </Button>

                        <div className="flex items-stretch gap-1">
                          <Input
                            placeholder={bg ? "Причина за отказ…" : "Reject reason…"}
                            value={rejectReason[item.id] || ""}
                            onChange={e => setRejectReason(p => ({ ...p, [item.id]: e.target.value }))}
                            className="h-8 text-xs flex-1"
                            data-testid={`reject-reason-${item.id}`}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => reject(item)}
                            disabled={!!busy}
                            data-testid={`reject-btn-${item.id}`}
                          >
                            {busy === "rejecting" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MacroPill({ label, value, unit = "" }) {
  return (
    <div className="flex items-center justify-between px-2 py-1 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
      <span className="text-emerald-700 dark:text-emerald-400 font-semibold">{label}</span>
      <span className="tabular-nums">{value != null ? `${Number(value).toFixed(1)}${unit}` : "—"}</span>
    </div>
  );
}
